import React from 'react';
import type { Portfolio, Fund } from 'venn-api';
import { useHasFF } from 'venn-utils';
import { LegacyRelativePortal } from 'venn-ui-kit';
import type { SearchMenuItem } from '../search-menu';
import { MultiSelectSearch } from '../search-menu';
import { AllocationSearchContainer } from './allocation-search';

export interface FundSelectionResult {
  needsManualStrategyAssignment?: boolean;
}

interface AllocationPanelSearchProps {
  onCancel: () => void;
  onMultiSelectFund: (items: SearchMenuItem[]) => void;
  onSingleSelectFund: (
    item: SearchMenuItem,
    constituentId?: number,
  ) => Promise<FundSelectionResult | void> | FundSelectionResult | void;
  onStrategyAssignment?: (fund: Fund, strategyId: string, constituentId?: number) => void;
  strategy: Portfolio;
  nodeRef: React.RefObject<HTMLDivElement>;
}

export const AllocationPanelSearch: React.FC<AllocationPanelSearchProps> = ({
  onCancel,
  onMultiSelectFund,
  onSingleSelectFund,
  onStrategyAssignment,
  strategy,
  nodeRef,
}) => {
  const hasRCMPortfolioAllocatorStrategyEnforcement = useHasFF('rcm_portfolio_strategy_enforcement_ff');

  const boundingRect = nodeRef.current?.getBoundingClientRect();

  if (hasRCMPortfolioAllocatorStrategyEnforcement) {
    return (
      <AllocationSearchContainer
        nodeRef={nodeRef}
        onClose={onCancel}
        onSingleSelectFund={onSingleSelectFund}
        onStrategyAssignment={onStrategyAssignment}
        strategy={strategy}
      />
    );
  }

  const excludedItems = (strategy.children || [])
    .filter((child) => child.fund)
    .map(({ fund }) => ({
      id: fund!.id,
      type: 'FUND' as const,
    }));

  // non RCM without PA design updates
  return (
    <LegacyRelativePortal
      component="div"
      left={5}
      style={{ width: 700 }}
      className="investment-search allocator-tree-item-investment-search [&_.select\_\_control]:pt-[4px] [&_.select\_\_control]:h-auto"
    >
      <MultiSelectSearch
        shouldLimitSize={false}
        autofocus
        investmentsOnly
        boundingRect={boundingRect}
        onSelected={onMultiSelectFund}
        onBlur={onCancel}
        excludedItems={excludedItems}
        location="allocationPanel"
        privateAssetSearchMode="PUBLIC_ONLY"
      />
    </LegacyRelativePortal>
  );
};
